// src/components/TechnologySection.jsx
import React from "react";
import {
  Box,
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
} from "@mui/material";
import SensorsIcon from "@mui/icons-material/Sensors";
import MemoryIcon from "@mui/icons-material/Memory";
import VpnLockIcon from "@mui/icons-material/VpnLock";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import LinkIcon from "@mui/icons-material/Link";
import GavelIcon from "@mui/icons-material/Gavel";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import PolicyIcon from "@mui/icons-material/Policy";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import AutoGraphIcon from "@mui/icons-material/AutoGraph";
import TimelineIcon from "@mui/icons-material/Timeline";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import SectionHeader from "./SectionHeader";
import TechTag from "./TechTag";

const TechnologySection = () => (
  <Box id="technology" sx={{ py: { xs: 6, md: 8 } }}>
    <Container maxWidth="lg">
      <SectionHeader
        kicker="Technology"
        title="The Carbon Bridge Computing Stack"
        subtitle="Under the hood, Carbon Bridge Computing is a layered architecture that joins IoT, blockchain, and AI into a single verifiable pipeline."
      />
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6} md={3}>
          <Card
            className="glass-card glow"
            sx={{ borderRadius: 3, boxShadow: 1, height: "100%" }}>
            <CardContent sx={{ p: 3 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: "50%",
                  bgcolor: "#eef2ff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mb: 1,
                }}
              >
                <SensorsIcon color="primary" />
              </Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                Secure Device & Data Layer
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "text.secondary", mt: 1 }}
              >
                Whitelisted energy devices sign readings with hardware-backed
                keys. Data streams are encrypted, hashed and timestamped before
                leaving the site.
              </Typography>
              <Box sx={{ mt: 1 }}>
                <TechTag icon={<MemoryIcon fontSize="small" />} label="IoT meters" />
                <TechTag
                  icon={<VpnLockIcon fontSize="small" />}
                  label="Edge security"
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card 
            className="glass-card glow"
            sx={{ borderRadius: 3, boxShadow: 1, height: "100%" }}>
            <CardContent sx={{ p: 3 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: "50%",
                  bgcolor: "#eef2ff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mb: 1,
                }}
              >
                <AccountBalanceWalletIcon color="primary" />
              </Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                Multi-Chain Tokenisation Engine
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "text.secondary", mt: 1 }}
              >
                Energy-to-carbon logic runs in smart contracts. Tokens and NFTs
                represent carbon units with immutable provenance and cross-chain
                bridge support.
              </Typography>
              <Box sx={{ mt: 1 }}>
                <TechTag icon={<LinkIcon fontSize="small" />} label="EVM chains" />
                <TechTag
                  icon={<GavelIcon fontSize="small" />}
                  label="Smart contracts"
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card 
            className="glass-card glow"
            sx={{ borderRadius: 3, boxShadow: 1, height: "100%" }}>
            <CardContent sx={{ p: 3 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: "50%",
                  bgcolor: "#eef2ff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mb: 1,
                }}
              >
                <VerifiedUserIcon color="primary" />
              </Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                Proofs & Compliance Layer
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "text.secondary", mt: 1 }}
              >
                Zero-knowledge style proof flows and deterministic logs give
                auditors and regulators an independently verifiable trail for
                every certificate.
              </Typography>
              <Box sx={{ mt: 1 }}>
                <TechTag icon={<PolicyIcon fontSize="small" />} label="Audit trails" />
                <TechTag
                  icon={<FactCheckIcon fontSize="small" />}
                  label="Compliance APIs"
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card 
            className="glass-card glow"
            sx={{ borderRadius: 3, boxShadow: 1, height: "100%" }}>
            <CardContent sx={{ p: 3 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: "50%",
                  bgcolor: "#eef2ff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mb: 1,
                }}
              >
                <AutoGraphIcon color="primary" />
              </Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                AI & Forecasting Analytics
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "text.secondary", mt: 1 }}
              >
                Time-series models, portfolio analytics and “what-if” scenarios
                let you optimise abatement, procurement and certificate
                strategies from one dashboard.
              </Typography>
              <Box sx={{ mt: 1 }}>
                <TechTag icon={<TimelineIcon fontSize="small" />} label="Time-series" />
                <TechTag
                  icon={<LightbulbIcon fontSize="small" />}
                  label="Decision support"
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  </Box>
);

export default TechnologySection;
